import { useEffect, useState, useCallback } from "react";
import { Blocks, Check, Plus, Loader2, Clock, Radar, X } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import api from "@/lib/api";

const STATUS_LABEL = {
  active: { text: "Active", color: "#32D74B" },
  connected: { text: "Connected", color: "#32D74B" },
  pending: { text: "Connecting…", color: "#FFD60A" },
  not_connected: { text: "Not connected", color: "#8E8E93" },
};

export default function Integrations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/integrations/catalog");
      setItems(res.data.integrations || []);
    } catch { toast.error("Failed to load integrations"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Handle return from OAuth (?google=… / ?slack=…)
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const providers = [["google", "Google Workspace"], ["slack", "Slack"]];
    let hit = false;
    for (const [key, name] of providers) {
      const v = q.get(key);
      if (!v) continue;
      hit = true;
      if (v === "connected") toast.success(`${name} connected!`);
      else toast.error(`${name} connection failed or was cancelled.`);
    }
    if (hit) { window.history.replaceState({}, "", "/integrations"); load(); }
  }, [load]);

  const OAUTH_START = {
    google_workspace: "/integrations/google/start",
    slack: "/integrations/slack/start",
  };

  const connect = async (provider) => {
    if (OAUTH_START[provider]) {
      setBusy(provider);
      try {
        // REMINDER: DO NOT HARDCODE THE URL — window.location.origin keeps the redirect env-correct.
        const res = await api.get(OAUTH_START[provider], { params: { origin: window.location.origin } });
        if (res.data.url) { window.location.href = res.data.url; return; }
        toast.error("Could not start the connection");
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not start the connection");
      }
      setBusy(null);
      return;
    }
    setBusy(provider);
    try {
      const res = await api.post(`/integrations/${provider}/connect`);
      toast.success(res.data.message || "Connection requested");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not connect");
    } finally { setBusy(null); }
  };

  const OAUTH_DISCONNECT = {
    google_workspace: "/integrations/google/disconnect",
    slack: "/integrations/slack/disconnect",
  };

  const disconnect = async (provider) => {
    setBusy(provider);
    try {
      await api.post(OAUTH_DISCONNECT[provider] || `/integrations/${provider}/disconnect`);
      toast.success("Disconnected");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not disconnect");
    } finally { setBusy(null); }
  };

  const categories = [...new Set(items.map((i) => i.category))];

  return (
    <Layout>
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <Blocks size={18} className="text-[color:var(--ws-accent,#4da3ff)]" />
          <p className="text-[color:var(--ws-accent,#4da3ff)] text-xs font-semibold uppercase tracking-[0.2em]">Marketplace</p>
        </div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-white" data-testid="integrations-title">Add integrations</h1>
        <p className="text-zinc-400 text-base mt-1">Connect the tools your business already uses. Everything feeds one command center.</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#007AFF]" /></div>
      ) : (
        <div className="space-y-8">
          {categories.map((cat) => (
            <div key={cat} data-testid={`integrations-category-${cat.toLowerCase()}`}>
              <h2 className="text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-3">{cat}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.filter((i) => i.category === cat).map((i) => {
                  const st = i.connectionStatus;
                  const comingSoon = i.status === "coming_soon";
                  const connected = st === "active" || st === "connected" || st === "pending";
                  return (
                    <div key={i.provider} data-testid={`integration-${i.provider}`} className="bg-[#121212] border border-white/10 rounded-xl p-5 flex flex-col">
                      <div className="flex items-start justify-between">
                        <div className="w-11 h-11 rounded-lg bg-white/5 flex items-center justify-center">
                          {i.builtin ? <Radar size={20} className="text-[color:var(--ws-accent,#4da3ff)]" /> : <Blocks size={20} className="text-zinc-300" />}
                        </div>
                        {connected && (
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-full" style={{ color: STATUS_LABEL[st]?.color, background: `${STATUS_LABEL[st]?.color}1a` }}>
                            {st === "pending" ? <Clock size={11} /> : <Check size={11} />} {STATUS_LABEL[st]?.text}
                          </span>
                        )}
                        {comingSoon && (
                          <span className="text-[11px] font-medium px-2 py-1 rounded-full text-zinc-400 bg-white/5">Coming soon</span>
                        )}
                      </div>
                      <p className="text-white font-medium mt-3">{i.name}</p>
                      <p className="text-zinc-500 text-sm mt-1 flex-1">{i.description}</p>
                      {st === "connected" && i.connectedEmail && (
                        <p className="text-zinc-400 text-xs mt-2 truncate" data-testid={`connected-email-${i.provider}`}>Connected as {i.connectedEmail}</p>
                      )}
                      <div className="mt-4">
                        {i.builtin ? (
                          <span className="text-[#32D74B] text-sm inline-flex items-center gap-1.5"><Check size={14} /> Included</span>
                        ) : comingSoon ? (
                          <button disabled className="w-full text-sm text-zinc-500 border border-white/10 rounded-lg py-2 cursor-not-allowed">Coming soon</button>
                        ) : st === "connected" ? (
                          <button
                            data-testid={`disconnect-${i.provider}`}
                            onClick={() => disconnect(i.provider)}
                            disabled={busy === i.provider}
                            className="w-full bg-transparent border border-white/15 text-zinc-300 hover:bg-white/5 rounded-lg py-2 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60 transition-colors"
                          >
                            {busy === i.provider ? <Loader2 size={15} className="animate-spin" /> : <X size={15} />} Disconnect
                          </button>
                        ) : st === "not_connected" ? (
                          <button
                            data-testid={`connect-${i.provider}`}
                            onClick={() => connect(i.provider)}
                            disabled={busy === i.provider}
                            className="w-full btn-brand text-white rounded-lg py-2 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
                          >
                            {busy === i.provider ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Connect
                          </button>
                        ) : (
                          <span className="text-zinc-400 text-sm inline-flex items-center gap-1.5"><Clock size={14} /> Authorization pending</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
}
